import React, { useEffect, useState } from "react";
import Spacing from "../Spacing";
import { ChevronLeft, ChevronRight, Loader } from "lucide-react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Gender,
  exchangeDate,
  notify,
  usPhoneFormat,
  getPractice,
  postData,
  calculateAgeByDos,
} from "../../helpers";
import { Config } from "../../Config";
import { aiChartHPI, chatAssesment, createChart } from "../../services/aiCharting";
import BackBtn from "../Button/BackBtn";

export default function SummaryAICharting({selectedPracticeChange, fetchChangePracticeData}) {
  const [aiChartData, setAiChartData] = useState([]);
  const [patientId, setPatientId] = useState(null);
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customHpi, setCustomHpi] = useState(null);
  const [gptAssessment, setGptAssessment] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [chartIsDone, setChartIsDone] = useState(false);
  const queryParams = new URLSearchParams(location.search);

  let accountId = getPractice("account_id");
  let locationId = getPractice("location_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    locationId = selectedPracticeChange?.location_id;
    accountId = selectedPracticeChange?.account_id;
    navigate('/ai-charting');
  }
  const paramPatientId = queryParams?.get("patient_id");
  const paramChartId = queryParams?.get("chart_no");

  useEffect(() => {
    if (state) {
      setPatientId(state.patientid);
      getChartingData(accountId, locationId, state.patientid);
    } else if(paramPatientId != null) {
      setPatientId(paramPatientId);
      getChartingData(accountId, locationId, paramPatientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, locationId, state, paramPatientId]);

  const getChartingData = async (account_id, location_id, patient_id, date = new Date()) => {
    setDataLoading(true);
    const chartData = {
      account_id: account_id,
      location_id: location_id,
      patient_id: parseInt(patient_id, 10),
      chart_id: parseInt(paramChartId, 10),
    };
    try {
      const response = await createChart(chartData);

      if (response?.data) {
        if (response?.data?.status === "success") {
          const chartResult = response?.data?.chart_data;

          if(chartResult?.status === "Done"){
            setChartIsDone(true);
          }
          if(chartResult?.account_id !== accountId && chartResult?.location_id !== locationId){
            navigate('/ai-charting');
            return;
          }
          if (!chartResult?.medication_data || !chartResult?.reason_for_visit_data || !chartResult?.assessment_data) {
            navigate(`/ai-charting/create?patient_id=${paramPatientId}&chart_no=${chartResult?.id}`);
            return;
          }

          setAiChartData(chartResult);
          await getFirstLoad(chartResult);
          setDataLoading(false);

        } else {
          notify("error", response?.data?.message);
          window.history.back();
          return;
        }
      }
    } catch (error) {
      // console.log("🚀 ~ getChartingData ~ error:", error);
      window.history.back();
      return;
    }
  };

  const handleChartingCreate = (patient_id) => {
    navigate(`/ai-charting/create?patient_id=${patient_id}&chart_no=${aiChartData?.id}`);
  };

  const getFirstLoad = async (chartData) => {
    // HPI Load
    if (firstLoad && !customHpi) {
      try {
        const hpiData = {
          chart_id: chartData?.id,
          ask: chartData?.raw_hpi
        };
        const result = await aiChartHPI(hpiData);

        if (result?.data && result?.data?.status === "success") {
          setCustomHpi(result?.data?.response);
          console.log("%c ✔ HPI initialized successfully.", "color: #68d100");
        }
      } catch (error) {
        // console.log("🚀 ~ getAIChartHPI ~ error:", error);
      }
    }

    // Assesment Load
    if (firstLoad && !gptAssessment) {
      let gptData = [];
      for (const item of chartData?.assessment_data) {
        try {
          
          if (item.assessment || item.assessment.trim().toLowerCase() === "not found") {
            gptData.push({
              icd_name: item.icd_name,
              icd_code: item.icd_code,
              assessment: item.assessment,
            });
          } else {
            const aiAssesment = {
              chart_id: chartData?.id,
              icd_code: item.icd_code,
              assessment: item.assessment,
            };
            const AssesmentResult = await chatAssesment(aiAssesment);

            if (AssesmentResult?.data && AssesmentResult?.data?.status === "success") {
              gptData.push({
                icd_name: item?.icd_name,
                icd_code: item?.icd_code,
                assessment: AssesmentResult?.data?.response,
              });
            }
          }
        } catch (error) {
          // console.log("🚀 ~ getChatAssesment ~ error:", error);
        }
      }
      setGptAssessment(gptData);
      console.log("%c ✔ Assessment initialized successfully.", "color: #68d100");
    }
    setDataLoading(false);
    setFirstLoad(false);
  };

  const updateStatus = async () => {
    setIsSubmitting(true);
    const result = await postData(`${Config.apiServerUrl}/store/chart_status`, true, { chart_id: aiChartData?.id, status: 'Done' });
    if (result) {
      notify(result.status, result.message)
      setIsSubmitting(false);
      navigate('/ai-charting');
    }
  };

  let diagnosisNumberCounter = 0;
  useDocumentTitle("AI Chart View");

  return (
    <>
      <BackBtn href="/ai-charting" btnText="Back to AI charting" />
      <Spacing lg="0" sm="24" />
      {!chartIsDone &&(
        <>
          <div className="docpro_wrap">
            <div className="docpro_tab">
              <button
                type="button"
                className={`docpro_tab_item`}
                onClick={() => handleChartingCreate(patientId)}
              >
                Reason
              </button>
              <button type="button" className={`docpro_tab_item active`}>
                Summary
              </button>
            </div>
          </div>
          <Spacing lg="8" sm="8" />
        </>
      )}

      <div className="docpro_data_table_wrap">
        <div className="docpro_card relative" style={{ minHeight: '70vh' }}>
          {!dataLoading ? (
            <>
              <h2 className="docpro_fs_35 docpro_text_center">{getPractice("account_name")}</h2>
              <Spacing lg="8" sm="8" />

              <div className="docpro_grid docpro_grid_gap_10 docpro_grid_col_2">
                <div className="docpro_grid_item">
                  <strong>NAME : </strong>
                  {aiChartData ? aiChartData?.last_name : ""}{" "}
                  {aiChartData ? aiChartData?.first_name : ""}
                </div>
                <div className="docpro_grid_item">
                  <strong>GENDER : </strong>
                  {aiChartData ? Gender(aiChartData?.sex) : ""}
                </div>
                <div className="docpro_grid_item">
                  <strong>DOB : </strong>
                  {aiChartData ? exchangeDate(aiChartData?.dob) : ""}
                </div>
                <div className="docpro_grid_item">
                  <strong>AGE : </strong>
                  {aiChartData ? calculateAgeByDos(aiChartData?.dob, aiChartData?.dos) : ""}Y
                </div>
                <div className="docpro_grid_item">
                  <strong>PHONE : </strong>
                  {aiChartData ? usPhoneFormat(aiChartData?.mobile_phone) : ""}
                </div>
                <div className="docpro_grid_item">
                  <strong>DOS : </strong>
                  {aiChartData ? exchangeDate(aiChartData?.dos) : ""}
                </div>
              </div>
              <Spacing lg="10" sm="10" />
              <div>
                <strong>INSURANCE NAME : </strong>
                {aiChartData ?
                  aiChartData?.insurance?.name
                : ""
                }
              </div>
              <Spacing lg="10" sm="10" />
              <div>
                <strong>INSURANCE ID : </strong>
                {aiChartData ? aiChartData?.insurance_code : ""}
              </div>

              <Spacing lg="28" sm="28" />
              <div>
                <div className="docpro_fs_18 docpro_indigo_color">
                  CHIEF COMPLAINT
                </div>
                <div>
                  <Spacing lg="8" sm="8" />
                  <div className="docpro_fs_16">REASON FOR VISIT:</div>
                  <Spacing lg="8" sm="8" />
                  <ul>
                    {aiChartData?.reason_for_visit_data &&
                      Object.values(aiChartData?.reason_for_visit_data?.rfvData).map(
                        (rfv, index) => (
                          <li key={index}>
                            {rfv.rfv_name}
                            {rfv.extra_notes ? (
                              <p className="docpro_indigo_color">
                                {rfv.extra_notes}
                              </p>
                            ) : (
                              ""
                            )}
                          </li>
                        )
                      )}
                  </ul>
                  <div className="docpro_fs_16">HISTORY OF PRESENT ILLNESS:</div>
                  <Spacing lg="8" sm="8" />
                  <div>{customHpi}</div>
                  <Spacing lg="8" sm="8" />
                  <div>Any Hospitalization Since Last Visit: None</div>
                  <div>Any Self Referrals Since Last Visit: None</div>
                  <div>
                    {" "}How would you like your medicine to be dispensed: Tablet{" "}
                  </div>
                  <div>Old chart and medical record reviewed</div>
                  <div>
                    {" "}Lab work, Imaging studies reviewed and discussed with patient{" "}
                  </div>
                </div>
              </div>

              <Spacing lg="28" sm="28" />
              <div>
                <div className="docpro_fs_18 docpro_indigo_color">
                  DIAGNOSIS CODE:
                </div>
                <div>
                  <Spacing lg="8" sm="8" />
                  {aiChartData?.medication_data &&
                    Object.values(aiChartData?.medication_data).map((rfvItem, index) => (
                      <div key={index}>
                        {rfvItem.icd_data && (
                          <ul className="docpro_mb_0" style={{ listStyle: "none" }}>
                            {rfvItem.icd_data.map((icdItem) => (
                              <li key={icdItem.icd_id}>
                                <b>{`${++diagnosisNumberCounter}.`}</b>{" "}
                                {icdItem.icd_name} ({icdItem.icd_code})
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              <Spacing lg="28" sm="28" />
              <div>
                <div className="docpro_fs_18 docpro_indigo_color">
                  ASSESSMENT/PLAN:
                </div>
                <div>
                  <Spacing lg="8" sm="8" />
                  {gptAssessment &&
                    gptAssessment.map((assessment) => (
                      <div key={assessment.icd_code} className="mb-2">
                        <h5 className="docpro_fs_16 docpro_mb_0">
                          {assessment.icd_name}
                        </h5>
                        <Spacing lg="8" sm="8" />
                        <div style={{ whiteSpace: "pre-wrap", marginLeft: "15px" }}>
                          {assessment.assessment}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <Spacing lg="28" sm="28" />
              <div>
                <div className="docpro_fs_18 docpro_indigo_color">
                  REVIEW OF SYSTEMS:
                </div>
                <div>
                  <Spacing lg="8" sm="8" />
                  {aiChartData?.medication_data &&
                    Object.values(aiChartData?.medication_data).map((rfvItem, index) => (
                      <div key={index}>
                        {rfvItem.ros_data && (
                          <>
                            {rfvItem.ros_data.map((rosItem) => (
                              <div key={rosItem.ros_id} className="mb-2">
                                <h5 className="docpro_fs_16 docpro_mb_0">
                                  {rosItem.ros_name}
                                </h5>
                                <Spacing lg="8" sm="8" />
                                <div style={{ whiteSpace: "break-spaces", marginLeft: "15px" }}>
                                  {rosItem.ros_details}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              <Spacing lg="28" sm="28" />
              <div>
                <div className="docpro_fs_18 docpro_indigo_color">
                  PHYSICAL EXAM:
                </div>
                <div>
                  <Spacing lg="8" sm="8" />
                  {aiChartData?.medication_data &&
                    Object.values(aiChartData?.medication_data).map((rfvItem, index) => (
                      <div key={index}>
                        {rfvItem.pe_data && (
                          <>
                            {rfvItem.pe_data.map((peItem) => (
                              <div key={peItem.pe_id} className="mb-2">
                                <h5 className="docpro_fs_16 docpro_mb_0">
                                  {peItem.pe_name}
                                </h5>
                                <Spacing lg="8" sm="8" />
                                <div style={{ whiteSpace: "break-spaces", marginLeft: "15px" }}>
                                  {peItem.pe_details}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              <Spacing lg="20" sm="20" />
              {!chartIsDone &&(
                <div className="docpro_page_heading_wrap">
                  {!chartIsDone &&(
                    <button
                      className="docpro_bordered_btn docpro_fw_600 size_lg"
                      onClick={() => handleChartingCreate(patientId)}
                    >
                      <ChevronLeft width={20} height={20} />
                      Back
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={updateStatus}
                    disabled={isSubmitting}
                    className="docpro_bordered_btn docpro_fw_600 docpro_white_color docpro_filed_btn size_lg"
                  >
                    Done
                    <ChevronRight width={20} height={20} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="absolute top-0 left-0 w-full" style={{ height: '100%', zIndex: 3 }}>
              <div className="flex items-center loading" style={{ width: '100%', height: '100%', justifyContent: 'center', background: 'rgb(255 255 255 / 63%)', borderRadius: '10px' }}>
                <span className="docpro_loading" style={{ position: 'inherit' }}>
                  <Loader width={46} height={46} style={{ color: 'var(--accent)' }} />
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
